<template>
  <Suspense suspensible>
    <Drag
      :data="{
        task: {
          id: taskId,
          canAllocate: taskInline?.task?.canAllocate ?? true,
          canMove: taskInline?.task?.canMove ?? true,
          isBound,
        },
      }"
      type="task"
    >
      <TaskInline
        ref="taskInline"
        v-bind="$attrs"
        :task-id="taskId"
        :actions="actions"
        :is-selected="isSelected"
        :is-editing="isEditing"
        :hide-scheduled-date="hideScheduledDate"
        :reorderable="reorderable"
        @select="$emit('select')"
        @set-selected-task-el="(el) => $emit('setSelectedTaskEl', el)"
        @quit-editing="$emit('quitEditing')"
      />
    </Drag>
  </Suspense>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from "vue";
import { Drag } from "vue-easy-dnd";

import type { GeneralizedTask, GeneralizedTaskId } from "@ctrl/controller-rpc";

import { defaultTaskActions, TaskActions } from "@/components/task/types";
import TaskInline from "@/components/TaskInline.vue";
import { isBoundTask } from "@/task";
import { isBoundTaskId } from "@/utils/id";

export default defineComponent({
  name: "TaskInlineDraggable",
  components: { Drag, TaskInline },
  inheritAttrs: false,
  props: {
    taskId: { type: String as PropType<GeneralizedTaskId>, required: true },
    reorderable: { type: Boolean, default: false },
    actions: {
      type: Object as PropType<TaskActions>,
      default: defaultTaskActions,
    },
    isSelected: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    hideScheduledDate: { type: Boolean, default: false },
  },
  emits: {
    select: () => true,
    setSelectedTaskEl: (_el: HTMLElement) => true,
    quitEditing: () => true,
  },
  setup(props) {
    const taskInline = ref<{ task: GeneralizedTask | null } | null>(null);
    // Until the inner TaskInline resolves its task record (which carries
    // `binding`), fall back to the id shape — it already identifies bound
    // database rows.
    const isBound = computed(() => {
      const task = taskInline.value?.task;
      return task ? isBoundTask(task) : isBoundTaskId(props.taskId);
    });
    return { taskInline, isBound };
  },
});
</script>
